/**
 * Doc content contract (V4) and the pure readers every caller shares.
 *
 * `blocks` (BlockNote JSON) is the source of truth once a person has edited
 * the Doc; `markdown` and `text` are snapshots written with every save. Agents
 * and Python write `{markdown}` only, so `blocks` may be missing.
 *
 * No BlockNote import here: this module is safe for the main chunk.
 */
import { markdownToPlainText } from "@/lib/markdown-plain-text";
import { noteContentToText } from "@/components/shapes/shared/note-content-text";

export const DOC_CONTENT_VERSION = 4;
/**
 * Largest HTML one page block may hold (UTF-8 bytes), so a single page copy
 * cannot push a save toward the 2 MB content cap. Mirrors
 * `DOC_PAGE_HTML_MAX_BYTES` in `apps/spaces/doc_writes.py`.
 */
export const DOC_PAGE_HTML_MAX_BYTES = 500_000;

export interface DocContent {
  version: typeof DOC_CONTENT_VERSION;
  /** Server-owned; bumped on every content write. */
  revision: number;
  /** BlockNote JSON; absent after a markdown-only (agent) write. */
  blocks?: unknown[];
  markdown: string;
  text: string;
}

type FrameContent = Record<string, unknown> | undefined;

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function isDocContent(
  content: FrameContent,
): content is DocContent & Record<string, unknown> {
  return content?.version === DOC_CONTENT_VERSION;
}

/** The stored revision of V4 content; 0 for legacy content. */
export function docRevision(content: FrameContent): number {
  if (!isDocContent(content)) return 0;
  return typeof content.revision === "number" ? content.revision : 0;
}

/** Markdown source of a legacy markdown frame (older rows only have `text`). */
function legacyMarkdown(content: Record<string, unknown>): string {
  return asString(content.markdown) || asString(content.text);
}

/**
 * True when legacy content is in a note format (V1 text/todos, V2 blocks,
 * V3 TipTap), read by its shape: a note migrated to a Doc keeps its content.
 * Bare `{markdown}` (an older agent write) is read as markdown, as is any
 * content of a frame still typed `markdown`.
 */
export function isLegacyNoteContent(
  frameType: string,
  content: FrameContent,
): boolean {
  if (frameType === "markdown" || !content || isDocContent(content)) {
    return false;
  }
  const bareMarkdown =
    typeof content.markdown === "string" &&
    content.version === undefined &&
    content.text === undefined &&
    content.todos === undefined;
  return !bareMarkdown;
}

/**
 * Markdown of any content a Doc can open. Legacy notes give their
 * markdown-compatible plain text (lists, checklists); the exact markdown of an
 * edited note comes from the save snapshot once it is a Doc.
 */
export function docMarkdown(frameType: string, content: FrameContent): string {
  if (!content) return "";
  if (isDocContent(content)) return asString(content.markdown);
  if (isLegacyNoteContent(frameType, content)) {
    return noteContentToText(content);
  }
  return legacyMarkdown(content);
}

/** Plain text of any content a Doc can open, for excerpts and fallbacks. */
export function docPlainText(frameType: string, content: FrameContent): string {
  if (!content) return "";
  if (isDocContent(content)) {
    return asString(content.text) || markdownToPlainText(content.markdown);
  }
  if (isLegacyNoteContent(frameType, content)) {
    return noteContentToText(content);
  }
  return markdownToPlainText(legacyMarkdown(content));
}

/**
 * V4 content without the server-owned `revision`, so a save and its echo
 * compare equal although the server bumped the revision. Legacy content is
 * returned unchanged.
 */
export function comparableDocContent(
  content: Record<string, unknown>,
): Record<string, unknown> {
  if (!isDocContent(content)) return content;
  const comparable: Record<string, unknown> = { ...content };
  delete comparable.revision;
  return comparable;
}

/**
 * Content of a new Doc from markdown. The server derives the stored `text`
 * and owns `revision`; a markdown-only write never carries blocks.
 */
export function newDocContent(markdown = ""): DocContent {
  return {
    version: DOC_CONTENT_VERSION,
    revision: 0,
    markdown,
    text: markdownToPlainText(markdown),
  };
}
